/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext } from "react";
import { Link } from "react-router-dom";
import {
  ArrowDownToLine,
  ArrowRightFromLine,
  ArrowUpFromLine,
  Check,
  Clock,
  Copy,
  FileText,
  Image as ImageIcon,
  MessageSquare,
  ShoppingCart,
  WrapText,
  X,
} from "lucide-react";
import { ErrorModalContext } from "../../../AlertsModal/AlertsModal";
import { formatCurrency } from "../../../../utils/moneyFormat";
import { formatDate } from "../../../../utils/dates";
import { getSignedDownloadUrl } from "../../../../services/files";
import {
  getTicketCardActionBody,
  getTicketCardTypeBody,
} from "../../../../translates/ticketsCards";

import { getTranslatedSaleStatus } from "../../../../translates/sales/sales";
import { FieldType, Space, TdProps } from "../interfaces";
import { TableBillStatus } from "../../../../interfaces/TableBills";
import { GroupConfig } from "../../../../interfaces/concessions";
import { Switch } from "../../Form/component/Switch";
import { profiles } from "../../../../profiles";
import { translateUserProfiles } from "../../../../translates/users/profiles";
import {
  MovementType,
  MovementTypeNames,
} from "../../../../interfaces/inventoryTransfer";
import { InventoryMovement } from "../../../../interfaces/inventoryMovements";
import {
  TransactionStatus,
  TransactionType,
} from "../../../../screens/SuperAdmin/Transactions/BankCards/components";
import { CashlessStatus } from "../../../../screens/Cashless/TicketsCards/utils/cashlessEnum";

export const Td: React.FC<TdProps> = ({
  align = "left",
  firstFieldType = "text",
  firstFieldValue,
  hasParent = false,
  isFirstFieldSubtitle = false,
  isSecondFieldSubtitle = false,
  minWidth = 100,
  openCommentModal,
  redirectToConcessions,
  row,
  secondFieldType = "text",
  secondFieldValue,
  space,
  textColor,
  venueUUID,
  weight = 1,
  onFunction,
}) => {
  const { showAlert } = useContext(ErrorModalContext);

  return (
    <td
      className={`flex-1 p-4 ${
        space === "body" ? "font-normal" : "font-semibold"
      } text-sm ${textColor || "text-gray-900"}`}
      style={{ minWidth: `${minWidth}px`, textAlign: align, flex: weight }}
    >
      <div className={`${hasParent ? "flex items-center gap-2 pl-4" : ""} ${
        firstFieldType === "quantity-with-pending" && align === "right"
          ? "flex justify-end"
          : ""
      }`}>
        {hasParent && <span className="text-gray-400">—</span>}
        <div className={`flex flex-col gap-1 ${
          firstFieldType === "quantity-with-pending" && align === "right"
            ? "w-full"
            : ""
        }`}>
          {formatContent(
            firstFieldValue,
            firstFieldType,
            isFirstFieldSubtitle,
            hasParent,
            space,
            showAlert,
            redirectToConcessions,
            venueUUID,
            openCommentModal,
            row,
            onFunction,
          )}
          {(secondFieldValue || secondFieldValue === 0) &&
            formatContent(
              secondFieldValue,
              secondFieldType,
              isSecondFieldSubtitle,
              hasParent,
              space,
              showAlert,
              undefined,
              undefined,
              openCommentModal,
              row,
            )}
        </div>
      </div>
    </td>
  );
};

const formatContent = (
  value: string | number | any,
  type: FieldType,
  isSubtitle: boolean,
  hasParent: boolean,
  space: Space,
  showAlert: (
    message: string,
    type: "success" | "error",
    title?: string,
  ) => void,
  redirectToConcessions?: (venueUUID: string) => void,
  venueUUID?: string,
  openCommentModal?: (row: any) => void,
  row?: any,
  onFunction?: (value: any) => void,
): JSX.Element => {
  if (space === "footer" && (value ?? "") === "") return <span></span>;

  if (type === "money")
    return (
      <span
        className={`break-words ${isSubtitle ? "text-xs text-gray-500" : ""}`}
      >
        {formatCurrency(typeof value === "string" ? Number(value) : value)}
      </span>
    );

  if (type === "cashless-status")
    return (
      <div className="flex items-center justify-center">
        <div
          className={`rounded-full ${isSubtitle ? "h-2 w-2" : "h-4 w-4"} ${
            value === CashlessStatus.ACTIVE
              ? "bg-green-500"
              : value === CashlessStatus.INACTIVE || value === CashlessStatus.CANCELLED
                ? "bg-gray-300"
                : "bg-yellow-500"
          }`}
        ></div>
      </div>
    );

  if (type === "boolean")
    return (
      <div className="flex items-center justify-center">
        <div
          className={`rounded-full ${isSubtitle ? "h-2 w-2" : "h-4 w-4"} ${
            value ? "bg-green-500" : "bg-gray-300"
          }`}
        ></div>
      </div>
    );

  if (type === "percentage")
    return (
      <span
        className={`break-words ${isSubtitle ? "text-xs text-gray-500" : ""}`}
      >
        {`${Number(value || 0).toFixed(2)}%`}
      </span>
    );

  if (type === "check") {
    if (value === null || value === undefined) {
      return <span></span>;
    }

    if (value === 0) {
      return (
        <div className="flex items-center justify-center">
          <X className="h-5 w-5 text-gray-500" />
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center">
        <Check className="h-5 w-5 text-green-500" />
      </div>
    );
  }

  if (type === "date")
    return (
      <span
        className={`break-words ${isSubtitle ? "text-xs text-gray-500" : ""}`}
      >
        {formatDate(value.toString())}
      </span>
    );

  if (type === "copy-paste") {
    return (
      <div className="flex items-center gap-2 font-semibold">
        <span className="break-words">{value}</span>
        {value && (
          <Copy
            className="h-4 w-4 shrink-0 cursor-pointer text-gray-500 transition-colors hover:text-gray-800"
            onClick={() => {
              navigator.clipboard.writeText(value.toString());
              showAlert(
                "Valor copiado correctamente",
                "success",
                "Copiado al portapapeles",
              );
            }}
          />
        )}
      </div>
    );
  }

  if (type === "session-counter") {
    if (value.toString().includes("Sesión Terminada"))
      return (
        <span className="break-words text-xs text-gray-500">
          Sesión Terminada
        </span>
      );

    if (value.toString().includes("Inicia"))
      return <span className="break-words text-xs text-blue-500">{value}</span>;

    const counter: number = parseInt(value.toString());

    return (
      <span
        className={`break-words text-xs ${
          counter === 0
            ? "text-gray-500"
            : counter < 10
              ? "text-orange-500"
              : counter < 30
                ? "text-yellow-500"
                : "text-green-600"
        }`}
      >
        {value}
      </span>
    );
  }

  if (type === "ticket-card-action") {
    const config = getTicketCardActionBody(value as string);

    return (
      <div className="flex items-center justify-center">
        <span
          className={`inline-block w-fit min-w-[80px] rounded-full px-3 py-1 text-xs font-normal
           ${config.tagColor} ${config.tagTextColor}`}
        >
          {config.tagText}
        </span>
      </div>
    );
  }

  if (type === "ticket-card-type") {
    const config = getTicketCardTypeBody(value as number);

    return (
      <div className="flex items-center justify-center">
        <span
          className={`inline-block w-fit min-w-[80px] rounded-full px-3 py-1 text-xs font-normal
           ${config.tagColor} ${config.tagTextColor}`}
        >
          {config.tagText}
        </span>
      </div>
    );
  }

  if (type === "bill-status") {
    return (
      <span
        className={`mx-auto w-fit break-words rounded-full px-3 py-1 text-xs font-medium ${
          value === 0
            ? "bg-red-100 text-red-800"
            : value === 1
              ? "bg-blue-100 text-blue-800"
              : value === 2
                ? "bg-gray-100 text-gray-800"
                : value === 3
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-green-100 text-green-800"
        }`}
      >
        {TableBillStatus[value]}
      </span>
    );
  }

  if (type === "sale-status") {
    const statusText = getTranslatedSaleStatus(Number(value));
    let bgColor = "bg-gray-100 text-gray-800";

    switch (value) {
      case 0:
        bgColor = "bg-red-100 text-red-800";
        break;
      case 1:
        bgColor = "bg-blue-100 text-blue-800";
        break;
      case 2:
        bgColor = "bg-green-100 text-green-800";
        break;
      case 3:
        bgColor = "bg-yellow-100 text-yellow-800";
        break;
      case 4:
        bgColor = "bg-red-100 text-red-800";
        break;
      case 5:
        bgColor = "bg-blue-100 text-blue-800";
        break;
      case 6:
        bgColor = "bg-gray-100 text-gray-800";
        break;
      case 7:
        bgColor = "bg-red-100 text-red-800";
        break;
      case 8:
        bgColor = "bg-orange-100 text-orange-800";
        break;
      default:
        bgColor = "bg-gray-100 text-gray-800";
        break;
    }

    return (
      <span
        className={`mx-auto w-fit break-words rounded-full px-3 py-1 text-xs font-medium ${bgColor}`}
      >
        {statusText}
      </span>
    );
  }
  if (type === "concessions-count-color" && typeof value === "string") {
    return (
      <span className="flex flex-row items-center  justify-end gap-3">
        <span
          className="hover:underline"
          onClick={() => redirectToConcessions?.(venueUUID ?? "")}
        >
          {value.split("_")[0]}
        </span>
        <span
          className={`h-2 w-2 rounded-full ${
            value.split("_")[1] === "0" ? "bg-green-500" : "bg-orange-500"
          }`}
        />
      </span>
    );
  }

  if (type === "fee-payer") {
    return (
      <span
        className={`mx-auto inline-block rounded-full px-2 py-1 text-center text-xs font-medium ${
          !value || typeof value !== "string"
            ? ""
            : value === "Concesión"
              ? "bg-purple-100 text-purple-800"
              : value === "Ambos"
                ? "bg-orange-100 text-orange-800"
                : value === "Cliente"
                  ? "bg-blue-100 text-blue-800"
                  : ""
        }`}
      >
        {value}
      </span>
    );
  }

  if (type === "user-profile") {
    return (
      <span
        className={`mx-auto inline-block break-words rounded-full px-3 py-1 text-center text-xs font-medium ${
          value === profiles.superAdmin || value === profiles.consultation
            ? "bg-red-100 text-red-800"
            : value === profiles.venuesAdmin
              ? "bg-blue-100 text-blue-800"
              : value === profiles.concessionAdmin || value === profiles.pos
                ? "bg-green-100 text-green-800"
                : value === profiles.recharges || value === profiles.seller
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-gray-100 text-gray-800"
        }`}
      >
        {translateUserProfiles[value]}
      </span>
    );
  }

  if (type === "liquidation-method") {
    return (
      <span
        className={`mx-auto inline-block rounded-full px-2 py-1 text-center text-xs font-medium ${
          value === "PPD"
            ? "bg-purple-100 text-purple-800"
            : "bg-green-100 text-green-800"
        }`}
      >
        {value}
      </span>
    );
  }

  if (type === "payments-accepted" && typeof value === "string" && value) {
    const paymentsAccepted: string[] = value.split(", ");

    return (
      <span className="flex flex-wrap items-center gap-2">
        {paymentsAccepted.map((payment) => (
          <span
            key={payment}
            className={`inline-block rounded-full px-2 py-1 text-center text-xs font-medium ${
              payment === "Tarjeta"
                ? "bg-green-100 text-green-800"
                : payment === "QR"
                  ? "bg-purple-100 text-purple-800"
                  : payment === "NFC"
                    ? "bg-blue-100 text-blue-800"
                    : "bg-orange-100 text-orange-800"
            }`}
          >
            {payment}
          </span>
        ))}
      </span>
    );
  }

  if (type === "discount-type") {
    return (
      <span
        className={`mx-auto inline-block rounded-full px-2 py-1 text-center text-xs font-medium ${
          value === "Fijo"
            ? "bg-orange-100 text-orange-800"
            : "bg-blue-100 text-blue-800"
        }`}
      >
        {value}
      </span>
    );
  }
  if (type === "discount-scope") {
    return (
      <span
        className={`mx-auto inline-block rounded-full px-2 py-1 text-center text-xs font-medium ${
          value === "Global"
            ? "bg-purple-100 text-purple-800"
            : "bg-green-100 text-green-800"
        }`}
      >
        {value}
      </span>
    );
  }
  if (type === "groups" && value) {
    const groups: GroupConfig[] = JSON.parse(value as string);

    return (
      <div className="flex flex-wrap items-center gap-2">
        {groups.map((group: GroupConfig) => (
          <span
            key={group.name}
            className="rounded-full px-2 py-1 text-center text-xs font-medium"
            style={{
              backgroundColor: group.bg_color,
              color: group.text_color,
            }}
          >
            {group.name}
          </span>
        ))}
      </div>
    );
  }

  if (type === "warehouse-scope") {
    const warehouses: string[] = value;

    return (
      <span className="flex flex-wrap items-center gap-2">
        {warehouses.length === 0 ? (
          <span className="inline-block rounded-full border border-green-200 bg-green-50 px-2 py-1 text-center text-xs font-medium text-green-700">
            General
          </span>
        ) : (
          <>
            {warehouses.slice(0, 3).map((warehouse, index) => (
              <span
                key={index}
                className="inline-block rounded-full border border-gray-200 bg-white px-2 py-1 text-center text-xs font-medium text-gray-800"
              >
                {warehouse}
              </span>
            ))}
            {warehouses.length > 3 && (
              <span className="inline-block rounded-full border border-gray-200 bg-white px-2 py-1 text-center text-xs font-medium text-gray-800">
                {`${warehouses.length - 3} más`}
              </span>
            )}
          </>
        )}
      </span>
    );
  }

  if (type === "quantity-with-pending") {
    const hasPendingTransactions = row?.has_pending_transactions || false;

    return (
      <div className="group/quantity relative flex w-full items-center justify-end gap-2">
        <span
          className={`break-words ${
            hasPendingTransactions ? "text-amber-600" : ""
          }`}
        >
          {value}
        </span>
        {hasPendingTransactions && (
          <>
            <Clock className="h-4 w-4 shrink-0 text-amber-600" />
            <div className="absolute top-full right-0 z-10 mt-2 hidden w-64 rounded-md bg-gray-900 px-3 py-2 text-xs text-white shadow-lg group-hover/quantity:block">
              <p className="font-medium">Transacciones pendientes</p>
              <p className="mt-1 text-gray-300">
                La disponibilidad muestra cantidades que ya fueron solicitadas
                pero aún no han sido aprobadas por el almacén destino.
              </p>
            </div>
          </>
        )}
      </div>
    );
  }

  if (type === "movement-type") {
    const movementType = value as MovementType;

    return (
      <span
        className={`inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${
          movementType === "entry"
            ? "border-green-200 bg-green-50 text-green-700"
            : movementType === "exit"
              ? "border-pink-200 bg-pink-50 text-pink-700"
              : movementType === "transfer"
                ? "border-blue-200 bg-blue-50 text-blue-700"
                : movementType === "sale"
                  ? "border-purple-200 bg-purple-50 text-purple-700"
                  : movementType === "sale_cancel"
                    ? "border-red-200 bg-red-50 text-red-700"
                    : "border-yellow-200 bg-yellow-50 text-yellow-700"
        }`}
      >
        {movementType === "entry" ? (
          <ArrowDownToLine className="h-3.5 w-3.5" />
        ) : movementType === "exit" ? (
          <ArrowUpFromLine className="h-3.5 w-3.5" />
        ) : movementType === "transfer" ? (
          <ArrowRightFromLine className="h-3.5 w-3.5" />
        ) : movementType === "sale" ? (
          <ShoppingCart className="h-3.5 w-3.5" />
        ) : movementType === "sale_cancel" ? (
          <X className="h-3.5 w-3.5" />
        ) : (
          <WrapText className="h-3.5 w-3.5" />
        )}
        {MovementTypeNames[movementType]}
      </span>
    );
  }

  const getValue = (
    value: string | number | undefined,
    fieldType?: string,
  ): string => {
    if (typeof value === "number") return value.toLocaleString();
    if (typeof value === "undefined") return "-";
    if (value === "") return "-";
    if (value === null) return "-";

    // Don't transform text-no-capitalize fields
    if (fieldType === "text-no-capitalize") return value;

    if (/^[A-Z0-9]+$/.test(value) && !isSubtitle) return value.toUpperCase();
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  };

  if (type === "movement-comment") {
    const movement = row as InventoryMovement;
    const isSaleOrCancelMovement: boolean =
      movement.movement_type === "sale" ||
      movement.movement_type === "sale_cancel";
    const hasCommentOrFile = !!(movement.comment || movement.urls?.length);

    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="break-words capitalize">{getValue(value)}</span>
        {!isSaleOrCancelMovement && hasCommentOrFile && (
          <button
            className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 transition-colors duration-300 hover:bg-gray-200"
            onClick={() => openCommentModal?.(movement)}
          >
            <MessageSquare className="h-3.5 w-3.5 shrink-0 text-gray-500" />
          </button>
        )}
      </div>
    );
  }

  if (type === "redirect-to-sale") {
    const movement = row as InventoryMovement;
    const [saleCode, saleUUID] = movement.comment
      ? movement.comment.split(" | ")
      : [];
    const isSaleOrCancelMovement: boolean =
      movement.movement_type === "sale" ||
      movement.movement_type === "sale_cancel";

    const redirectToSale: boolean = saleUUID ? saleUUID.includes("sal") : false;

    return (
      <div className="flex flex-col gap-1">
        <span className="break-words capitalize">{getValue(value)}</span>
        {isSaleOrCancelMovement && saleCode && saleUUID && (
          <Link
            to={`/panel/${redirectToSale ? "ordenes" : "cuentas"}/${saleUUID}`}
            className="w-fit max-w-[218px] truncate text-xs text-gray-500 underline transition-colors duration-300 hover:text-gray-700"
          >
            {`${redirectToSale ? "Venta" : "Cuenta"}: ${saleCode}`}
          </Link>
        )}
      </div>
    );
  }

  if (type === "card-number") {
    return (
      <span
        className={`inline-block break-words align-baseline font-mono leading-normal    ${
          isSubtitle
            ? "text-xs text-gray-500"
            : hasParent
              ? "text-gray-500"
              : ""
        }`}
      >
        {value}
      </span>
    );
  }

  if (type === "text-no-capitalize") {
    return (
      <span
        className={`inline-block break-words align-baseline  leading-normal    ${
          isSubtitle
            ? "text-xs text-gray-500"
            : hasParent
              ? "text-gray-500"
              : ""
        }`}
      >
        {value}
      </span>
    );
  }

  if (type === "function") {
    return (
      <span
        onClick={onFunction}
        className="cursor-pointer hover:underline"
      >
        {value}
      </span>
    );
  }

  if (type === "transaction-status") {
    return <TransactionStatus status={value as number} />;
  }

  if (type === "transaction-type") {
    return <TransactionType type={value as string} />;
  }

  if (type === "file-icons") {
    const rowData = row as any;
    const receiptUrl = rowData.receipt_url;
    const imageUrl = rowData.image_url;
    let isDownloading: string | null = null;

    const handleFileClick = async (fileUrl: string) => {
      try {
        isDownloading = fileUrl;
        const signedUrl = await getSignedDownloadUrl(fileUrl);
        window.open(signedUrl, "_blank");
      } catch (error) {
        console.error("Error getting signed URL:", error);
        // Fallback to direct URL if signed URL fails
        window.open(fileUrl, "_blank");
      } finally {
        isDownloading = null;
      }
    };

    return (
      <div className="flex items-center justify-center gap-3">
        {receiptUrl && (
          <button
            className="flex items-center justify-center transition-colors duration-300 hover:opacity-70"
            onClick={(e) => {
              e.stopPropagation();
              handleFileClick(receiptUrl);
            }}
            title="Ver comprobante"
            disabled={isDownloading !== null}
          >
            {isDownloading === receiptUrl ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"></div>
            ) : (
              <FileText className="h-4 w-4 text-gray-500" />
            )}
          </button>
        )}
        {imageUrl && (
          <button
            className="flex items-center justify-center transition-colors duration-300 hover:opacity-70"
            onClick={(e) => {
              e.stopPropagation();
              handleFileClick(imageUrl);
            }}
            title="Ver evidencia"
            disabled={isDownloading !== null}
          >
            {isDownloading === imageUrl ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"></div>
            ) : (
              <ImageIcon className="h-4 w-4 text-gray-500" />
            )}
          </button>
        )}
        {!receiptUrl && !imageUrl && (
          <span className="text-xs text-gray-400">Sin archivos</span>
        )}
      </div>
    );
  }

  if (type === "text-bold") {
    return (
      <span
        className={`break-words font-medium ${
          isSubtitle
            ? "text-xs text-gray-500"
            : hasParent
              ? "text-gray-500"
              : ""
        }`}
      >
        {value}
      </span>
    );
  }

  if (type === "beneficiary-status") {
    // Check if we should hide the dot completely
    if (row?.hideBeneficiaryStatus) {
      return <div className="flex items-center justify-center"></div>;
    }

    const hasBankInfo =
      row?.bank_information &&
      row.bank_information.beneficiary &&
      row.bank_information.beneficiary.trim() !== "" &&
      row.bank_information.bank_name &&
      row.bank_information.bank_name.trim() !== "" &&
      row.bank_information.main_account &&
      row.bank_information.main_account.trim() !== "";

    return (
      <div className="flex items-center justify-center">
        <div
          className={`mt-1.5 rounded-full ${
            isSubtitle ? "h-2 w-2" : "h-4 w-4"
          } ${hasBankInfo ? "bg-green-500" : "bg-red-500"}`}
        ></div>
      </div>
    );
  }

  if (type === "signal") {
    const [signalStrength, text] = value?.toString().split("_text_") || [
      "0",
      "0",
    ];

    const activeColor =
      signalStrength >= 50
        ? "green-500"
        : signalStrength >= 25
          ? "amber-500"
          : "red-500";

    if (signalStrength >= 75) {
      return (
        <div className="flex h-5 flex-row items-end gap-0.5">
          <div className={`h-[5px] w-1 rounded-sm bg-${activeColor}`}></div>
          <div className={`h-[8px] w-1 rounded-sm bg-${activeColor}`}></div>
          <div className={`h-[11px] w-1 rounded-sm bg-${activeColor}`}></div>
          <div className={`h-[14px] w-1 rounded-sm bg-${activeColor}`}></div>
          <span className="ml-1 text-xs">{text}</span>
        </div>
      );
    }

    if (signalStrength >= 50 && signalStrength < 75) {
      return (
        <div className="flex h-5 flex-row items-end gap-0.5">
          <div className={`h-[5px] w-1 rounded-sm bg-${activeColor}`}></div>
          <div className={`h-[8px] w-1 rounded-sm bg-${activeColor}`}></div>
          <div className={`h-[11px] w-1 rounded-sm bg-${activeColor}`}></div>
          <div className="h-[14px] w-1 rounded-sm bg-gray-300"></div>
          <span className="ml-1 text-xs">{text}</span>
        </div>
      );
    }

    if (signalStrength >= 25 && signalStrength < 50) {
      return (
        <div className="flex h-5 flex-row items-end gap-0.5">
          <div className={`h-[5px] w-1 rounded-sm bg-${activeColor}`}></div>
          <div className={`h-[8px] w-1 rounded-sm bg-${activeColor}`}></div>
          <div className={`h-[11px] w-1 rounded-sm bg-${activeColor}`}></div>
          <div className="h-[14px] w-1 rounded-sm bg-gray-300"></div>
          <span className="ml-1 text-xs">{text}</span>
        </div>
      );
    }

    return (
      <div className="flex h-5 flex-row items-end gap-0.5">
        <div className={`h-[5px] w-1 rounded-sm bg-${activeColor}`}></div>
        <div className="h-[8px] w-1 rounded-sm bg-gray-300"></div>
        <div className="h-[11px] w-1 rounded-sm bg-gray-300"></div>
        <div className="h-[14px] w-1 rounded-sm bg-gray-300"></div>
        <span className="ml-1 text-xs">{text}</span>
      </div>
    );
  }

  if (type === "battery") {
    const [batteryLevel, isCharging] = value
      ?.toString()
      .split("_ischarging_") || ["0", "0"];

    if (batteryLevel >= 65) {
      return (
        <div className="flex h-5 flex-row items-center gap-0.5">
          <div
            className={`h-3 w-2 rounded-md ${
              isCharging === "1" ? "bg-blue-500" : "bg-green-500"
            }`}
          ></div>
          <div
            className={`h-3 w-2 rounded-md ${
              isCharging === "1" ? "bg-blue-500" : "bg-green-500"
            }`}
          ></div>
          <div
            className={`h-3 w-2 rounded-md ${
              isCharging === "1" ? "bg-blue-500" : "bg-green-500"
            }`}
          ></div>
          <div
            className={`h-3 w-2 rounded-md ${
              isCharging === "1" ? "bg-blue-500" : "bg-green-500"
            }`}
          ></div>
          {isCharging === "1" && <span className="text-xs">⚡</span>}
        </div>
      );
    }
    if (batteryLevel >= 30 && batteryLevel < 65) {
      return (
        <div className="flex h-5 flex-row items-center gap-0.5">
          <div
            className={`h-3 w-2 rounded-md ${
              isCharging === "1" ? "bg-blue-500" : "bg-amber-500"
            }`}
          ></div>
          <div
            className={`h-3 w-2 rounded-md ${
              isCharging === "1" ? "bg-blue-500" : "bg-amber-500"
            }`}
          ></div>
          <div
            className={`h-3 w-2 rounded-md ${
              isCharging === "1" ? "bg-blue-500" : "bg-amber-500"
            }`}
          ></div>
          <div className="h-3 w-2 rounded-md bg-gray-300"></div>
          {isCharging === "1" && <span className="text-xs">⚡</span>}
        </div>
      );
    }

    if (batteryLevel >= 10 && batteryLevel < 30) {
      return (
        <div className="flex h-5 flex-row items-center gap-0.5">
          <div
            className={`h-3 w-2 rounded-md ${
              isCharging === "1" ? "bg-blue-500" : "bg-amber-500"
            }`}
          ></div>
          <div
            className={`h-3 w-2 rounded-md ${
              isCharging === "1" ? "bg-blue-500" : "bg-amber-500"
            }`}
          ></div>
          <div className="h-3 w-2 rounded-md bg-gray-300"></div>
          <div className="h-3 w-2 rounded-md bg-gray-300"></div>
          {isCharging === "1" && <span className="text-xs">⚡</span>}
        </div>
      );
    }

    return (
      <div className="flex h-5 flex-row items-center gap-0.5">
        <div
          className={`h-3 w-2 animate-[pulse_0.5s_ease-in-out_infinite] rounded-md ${
            isCharging === "1" ? "bg-blue-500" : "bg-red-500"
          }`}
        ></div>
        <div className="h-3 w-2 rounded-md bg-gray-300"></div>
        <div className="h-3 w-2 rounded-md bg-gray-300"></div>
        <div className="h-3 w-2 rounded-md bg-gray-300"></div>
        {isCharging === "1" && <span className="text-xs">⚡</span>}
      </div>
    );
  }

  if (type === "row-with-subtitle") {
    const [title, subtitle] = value?.toString().split("_text_") || ["", ""];

    return (
      <div className="flex flex-row items-baseline gap-1">
        <span className="text-sm ">{title}</span>
        <span className="text-xs text-gray-500">v. {subtitle || "N/A"}</span>
      </div>
    );
  }

  if (type === "latency-tag") {
    if (value === 0 || value === null) {
      return (
        <span className="inline-block w-fit self-end rounded-full bg-gray-500 px-4 py-1  text-xs font-medium text-white">
          Offline
        </span>
      );
    }
    if (value < 150) {
      return (
        <span className="inline-block w-fit self-end rounded-full bg-green-100 px-4 py-1  text-xs font-medium text-green-700">
          Buena
        </span>
      );
    }

    if (value < 300) {
      return (
        <div className="inline-block w-fit self-end rounded-full bg-amber-100 px-4 py-1  text-xs font-medium text-amber-700">
          regular
        </div>
      );
    }

    return (
      <div className="inline-block w-fit self-end rounded-full bg-red-100 px-4 py-1  text-xs font-medium text-red-700">
        mala
      </div>
    );
  }

  if (type === "primary-speed") {
    const [speed, category] = value?.toString().split("_text_") || ["", ""];

    if (category === "5") {
      return (
        <div className="flex flex-row items-baseline gap-1">
          <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-500">
            WIFI {category}.0
          </span>
          <span className="text-xs ">{speed} Mbps</span>
        </div>
      );
    }
    if (category === "2.4" || category === "2.4.0") {
      return (
        <div className="flex flex-row items-baseline gap-1">
          <span className="rounded-full bg-cyan-100 px-2 py-1 text-xs font-medium text-cyan-500">
            WIFI {category}
          </span>
          <span className="text-xs ">{speed} Mbps</span>
        </div>
      );
    }

    if (category === "0") {
      return (
        <div className="flex flex-row items-baseline gap-1">
          <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-500">
            Desconocido
          </span>
          <span className="text-xs ">{speed} Mbps</span>
        </div>
      );
    }

    return (
      <div className="flex flex-row items-baseline gap-1">
        <span className="rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-500">
          {category.toUpperCase()}
        </span>
        <span className="text-xs">{speed} Mbps</span>
      </div>
    );
  }

  if (type === "text-font-medium") {
    return (
      <span
        className={`break-words font-medium ${
          isSubtitle
            ? "text-xs text-gray-500"
            : hasParent
              ? "text-gray-500"
              : ""
        }`}
      >
        {value}
      </span>
    );
  }

  if (type === "comment-subtitle") {
    const hasValue = value && value.toString().trim() !== "";
    return (
      <span
        className={`break-words ${
          hasValue ? "text-xs text-gray-500" : ""
        }`}
      >
        {value || ""}
      </span>
    );
  }

  if (type === "row-actions") {
    return (
      <div
        className="flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {value}
      </div>
    );
  }

  if (type === "switch") {
    const rowData = row as any;
    const isLoading = rowData.isLoadingStatus || false;
    return (
      <div
        className="flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <Switch
          checked={Boolean(value)}
          onChange={() => {
            if (onFunction) {
              onFunction(row);
            }
          }}
          isLoading={isLoading}
        />
      </div>
    );
  }

  return (
    <span
      className={`break-words ${
        typeof value === "string" && value.includes(" hrs") ? "" : "capitalize"
      } ${
        isSubtitle ? "text-xs text-gray-500" : hasParent ? "text-gray-500" : ""
      }`}
    >
      {getValue(value, type)}
    </span>
  );
};
